import React, { useState } from "react";
import { mockCreator } from "../../../data/mockData";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import UploadMediaModal from "../components/modals/UploadMediaModal";
import { Upload, Edit2, X, Tag } from "lucide-react";

// --- Edit Modal ---
const EditMediaModal = ({ isOpen, onClose, mediaItem, onSave }) => {
  const [formData, setFormData] = useState({
    title: mediaItem?.fileName || "",
    description: "No description added.",
    tags: "music, ethiopia, art"
  });

  // Update form data when mediaItem changes
  React.useEffect(() => {
    if (mediaItem) {
      setFormData({
        title: mediaItem.fileName,
        description: mediaItem.description || "No description added.",
        tags: mediaItem.tags || "music, ethiopia, art"
      });
    }
  }, [mediaItem]);

  if (!isOpen || !mediaItem) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <div className="flex justify-between items-center p-4 border-b border-slate-100">
          <h3 className="font-bold text-lg text-slate-800">Edit Details</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={20} />
          </button>
        </div>
        
        <div className="p-6 flex flex-col gap-6">
          <div className="flex gap-4">
            <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 border border-slate-200">
               <img src={mediaItem.thumbnailUrl} alt={mediaItem.fileName} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input 
                  className="w-full text-sm border border-slate-200 rounded-lg p-2.5 focus:ring-2 focus:ring-indigo-500 outline-none"
                  value={formData.title}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                />
              </div>
              <div>
                 <label className="block text-sm font-medium text-slate-700 mb-1">Tags</label>
                 <div className="relative">
                   <input 
                     className="w-full text-sm border border-slate-200 rounded-lg p-2.5 pl-9 focus:ring-2 focus:ring-indigo-500 outline-none"
                     value={formData.tags}
                     onChange={e => setFormData({...formData, tags: e.target.value})}
                   />
                   <Tag size={14} className="absolute left-3 top-3 text-slate-400" />
                 </div>
              </div>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea 
               className="w-full text-sm border border-slate-200 rounded-lg p-3 h-24 focus:ring-2 focus:ring-indigo-500 outline-none resize-none"
               value={formData.description}
               onChange={e => setFormData({...formData, description: e.target.value})}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => onSave({ ...mediaItem, ...formData, fileName: formData.title })}>Save Changes</Button>
          </div>
        </div>
      </div>
    </div>
  );
};


// --- Media Card ---
const MediaItemCard = ({ item, onEdit }) => (
  <Card className="p-0 overflow-hidden group relative">
    <div className="aspect-square bg-slate-100 flex items-center justify-center relative">
      <img
        src={item.thumbnailUrl}
        alt={item.fileName}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
        <button 
          onClick={() => onEdit(item)}
          className="bg-white/90 hover:bg-white text-slate-900 px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 transform translate-y-2 group-hover:translate-y-0 transition-all"
        >
          <Edit2 size={14} /> Edit
        </button>
      </div>
    </div>
    <div className="p-3">
      <p className="text-sm font-medium text-slate-800 truncate" title={item.fileName}>
        {item.fileName}
      </p>
      <p className="text-xs text-slate-500 flex justify-between items-center mt-1">
        <span>{item.fileType}</span>
        <span>{item.uploadDate}</span>
      </p>
    </div>
  </Card>
);

const MediaLibraryPage = () => {
  // Enhanced Mock Data with Ethiopian Assets
  const [mediaItems, setMediaItems] = useState([
    {
      id: 101,
      fileName: "Addis Nights - Album Cover",
      fileType: "Image",
      uploadDate: "2024-10-28",
      thumbnailUrl: "/assets/addis_nights.png",
      description: "Final artwork for the new jazz fusion album.",
      tags: "jazz, album, cover, ethiopia"
    },
    {
      id: 102,
      fileName: "Meskel Vibes Concert",
      fileType: "Image",
      uploadDate: "2024-10-27",
      thumbnailUrl: "/assets/meskel_vibes.png",
      description: "Official promotional poster for the Meskel square concert.",
      tags: "concert, poster, event, addis"
    },
    {
      id: 103,
      fileName: "Studio Session - Behind Scenes",
      fileType: "Image",
      uploadDate: "2024-10-26",
      thumbnailUrl: "/assets/studio_session.png",
      description: "Candid shot from the recording session at soundlab.",
      tags: "studio, music, producer, bts"
    },
    {
       id: 104,
       fileName: "Singer Promo Portrait",
       fileType: "Image",
       uploadDate: "2024-10-25",
       thumbnailUrl: "/assets/singer_portrait.png",
       description: "High res promo shot for press releases.",
       tags: "portrait, singer, promo, fashion"
    },
    ...mockCreator.mediaLibrary
  ]);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filter, setFilter] = useState("All");

  const handleUpload = (newItem) => {
    setMediaItems([newItem, ...mediaItems]);
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = (updatedItem) => {
    setMediaItems(mediaItems.map(item => item.id === updatedItem.id ? updatedItem : item));
    setIsEditModalOpen(false);
    setSelectedItem(null);
  };

  const filteredMedia = mediaItems.filter((item) => {
    if (filter === "All") return true;
    return item.fileType === filter;
  });

  return (
    <>
    <div className="space-y-6 max-w-full overflow-hidden">
        <div className="flex flex-col @md:flex-row justify-between items-start @md:items-center gap-4">
          <div>
            <h1 className="text-2xl @sm:text-3xl font-bold text-slate-800">Media Library</h1>
            <p className="text-slate-500 mt-1 text-sm @sm:text-base">
              Manage all your reusable brand assets.
            </p>
          </div>
          {/* This button now opens the modal */}
          <Button variant="primary" onClick={() => setIsUploadModalOpen(true)} className="w-full @md:w-auto">
            <Upload size={16} className="mr-2" /> Upload Media
          </Button>
        </div>

        {/* Filter Controls */}
        <div className="flex space-x-4 border-b border-slate-100 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setFilter("All")}
            className={`pb-3 text-sm font-bold transition-all whitespace-nowrap ${
              filter === "All"
                ? "border-b-2 border-primary text-primary"
                : "text-slate-400 hover:text-slate-600"
            }`}
          >
            All Assets
          </button>
          <button
            onClick={() => setFilter("Image")}
            className={`pb-3 text-sm font-bold transition-all whitespace-nowrap ${
              filter === "Image"
                ? "border-b-2 border-primary text-primary"
                : "text-slate-400 hover:text-slate-600"
            }`}
          >
            Images
          </button>
          <button
            onClick={() => setFilter("Video")}
            className={`pb-3 text-sm font-bold transition-all whitespace-nowrap ${
              filter === "Video"
                ? "border-b-2 border-primary text-primary"
                : "text-slate-400 hover:text-slate-600"
            }`}
          >
            Videos
          </button>
        </div>

        {/* Media Grid */}
        <div className="grid grid-cols-2 @sm:grid-cols-3 @md:grid-cols-4 @lg:grid-cols-5 gap-4">
          {filteredMedia.map((item) => (
            <MediaItemCard key={item.id} item={item} onEdit={handleEditClick} />
          ))}
        </div>
      </div>

      <UploadMediaModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleUpload}
      />

      <EditMediaModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        mediaItem={selectedItem}
        onSave={handleSaveEdit}
      />
    </>
  );
};

export default MediaLibraryPage;
